import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Wrench,
  Plus,
  Filter,
  Download,
  Search,
  MoreVertical,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatPercent } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  const [units, setUnits] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [unitsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/units`),
        axios.get(`${API}/analytics/units`)
      ]);
      
      setUnits(unitsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (unit.tenant_name && unit.tenant_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'premium' && unit.unit_type === 'premium') ||
                      (activeTab === 'standard' && unit.unit_type === 'standard');
    return matchesSearch && matchesTab;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'occupied':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">● Occupied</Badge>;
      case 'vacant':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">● Vacant</Badge>;
      case 'maintenance':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">● Maintenance</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRentStatus = (status) => {
    if (!status) return <span className="text-slate-400">—</span>;
    switch (status) {
      case 'paid':
        return <span className="text-green-600 font-medium">Paid</span>;
      case 'overdue':
        return <span className="text-red-600 font-medium">Overdue</span>;
      case 'pending':
        return <span className="text-amber-600 font-medium">Pending</span>;
      default:
        return <span className="text-slate-500">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A56DB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Properties</span>
        <span className="text-slate-300">›</span>
        <span className="text-[#1A56DB] font-medium">Skyline Residential</span>
      </div>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Room Management
          </h1>
          <p className="text-slate-500 mt-1 max-w-xl">
            Monitor occupancy rates, manage maintenance cycles, and streamline tenant transitions across all units.
          </p>
        </div>
        <Button className="gap-2 bg-[#1A56DB] hover:bg-[#1E40AF]" data-testid="add-room-btn">
          <Plus className="w-4 h-4" />
          Add New Room
        </Button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Units */}
        <div className="metric-card" data-testid="metric-total-units">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1A56DB]" />
            </div>
            <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">
              +{analytics?.change}% this month
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mb-1">Total Units</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {analytics?.total_units || 0}
          </p>
        </div>

        {/* Occupancy Rate */}
        <div className="metric-card" data-testid="metric-occupancy">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Target: 98%</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Occupancy Rate</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {formatPercent(analytics?.occupancy_rate)}
          </p>
        </div>

        {/* Under Maintenance */}
        <div className="metric-card" data-testid="metric-maintenance">
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 text-red-500" />
            </div>
            <span className="text-xs text-red-500 font-medium">{analytics?.under_maintenance || 0} Urgent</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">Under Maintenance</p>
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {analytics?.under_maintenance || 0} Units
          </p>
        </div>
      </div>

      {/* Filters and Table */}
      <div className="chart-container">
        {/* Tabs and Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all" data-testid="tab-all-rooms">All Rooms</TabsTrigger>
              <TabsTrigger value="premium" data-testid="tab-premium">Premium Suites</TabsTrigger>
              <TabsTrigger value="standard" data-testid="tab-standard">Standard Units</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" className="gap-2" data-testid="filter-btn">
              <Filter className="w-4 h-4" />
              Filter
            </Button>
            <Button variant="outline" size="sm" className="gap-2" data-testid="export-btn">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Unit Info</th>
                <th>Tenant</th>
                <th>Status</th>
                <th>Rent Status</th>
                <th>Last Inspect.</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUnits.map((unit) => (
                <tr key={unit.id} data-testid={`unit-row-${unit.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {unit.image ? (
                        <img 
                          src={unit.image} 
                          alt={unit.name}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-slate-900">{unit.name}</p>
                        <p className="text-xs text-slate-500">{unit.unit_type === 'premium' ? '2B/2B' : '1B'} • Floor {unit.floor}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    {unit.tenant_name ? (
                      <div>
                        <p className="font-medium text-slate-900">{unit.tenant_name}</p>
                        <p className="text-xs text-slate-500">Since {unit.tenant_since}</p>
                      </div>
                    ) : (
                      <span className="text-slate-400 italic">None</span>
                    )}
                  </td>
                  <td>{getStatusBadge(unit.status)}</td>
                  <td>{getRentStatus(unit.rent_status)}</td>
                  <td className="text-slate-600">{unit.last_inspection || '—'}</td>
                  <td>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`unit-actions-${unit.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Details</DropdownMenuItem>
                        <DropdownMenuItem>Edit Unit</DropdownMenuItem>
                        <DropdownMenuItem>Schedule Inspection</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">Mark for Maintenance</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium">{filteredUnits.length}</span> of <span className="font-medium">{analytics?.total_units || 0}</span> units
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>Previous</Button>
            <Button variant="default" size="sm" className="bg-[#1A56DB]">1</Button>
            <Button variant="outline" size="sm">2</Button>
            <Button variant="outline" size="sm">3</Button>
            <Button variant="outline" size="sm">Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Properties;
